import { useState } from "react";
import { useReveal } from "../../hooks/useReveal";
import { useI18n } from "../../i18n/useI18n";
import { useAuth } from "../../lib/auth";
import { createTicket } from "../../lib/store";
import { submitContactMessage } from "../../lib/leads";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { GlassCard } from "../ui/GlassCard";
import { DynamicImage } from "../DynamicImage";
import { PAGE_IMAGE_SLOTS } from "../../lib/page-image-defaults";

const contactImage = PAGE_IMAGE_SLOTS.find((s) => s.pagePath === "/" && s.sectionName === "contact")!;

export const CONTACT_EMAIL = "contact@jyotish-astro.com";

export function Contact() {
  const scope = useReveal<HTMLElement>();
  const { t } = useI18n();
  const { session } = useAuth();
  const c = t.contact;
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // Dual-write: (1) the existing ticket/"Cosmic Chat" thread (same
      // Supabase table the Admin "Ticket-Inbox" reads), so a reply can be
      // sent back through the existing staff inbox UI; (2) a
      // contact_messages row + Brevo contact sync (see lib/leads.ts) for
      // marketing automations and the Admin "Leads" tab. Both are awaited;
      // if either genuinely fails, we still show the error (see below) —
      // but lib/leads.ts's own Brevo half is separately best-effort
      // internally, so only a real Supabase failure surfaces here.
      await Promise.all([
        createTicket({
          customerId: session?.user.id ?? null,
          customerName: form.name,
          customerEmail: form.email,
          subject: `Kontaktanfrage über ${CONTACT_EMAIL}`,
          offerTitle: null,
          firstMessage: form.message,
        }),
        submitContactMessage({ name: form.name, email: form.email, message: form.message }),
      ]);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Nachricht konnte nicht gesendet werden.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="contact" ref={scope} className="relative py-24 md:py-32">
      {/* Soft beige aura glow behind the contact card */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(50%_60%_at_50%_40%,rgba(212,175,55,0.10),transparent_65%)]"
      />
      <div className="container-lux max-w-2xl">
        <DynamicImage
          pagePath={contactImage.pagePath}
          sectionName={contactImage.sectionName}
          defaultImage={contactImage.defaultImage}
          alt={contactImage.alt}
          wrapperClassName="mx-auto mb-8 h-32 w-32 rounded-full shadow-glow"
          className="h-full w-full rounded-full object-cover"
        />
        <div className="text-center">
          <p className="eyebrow mb-4" data-reveal>{c.eyebrow}</p>
          <h2 className="text-3xl font-semibold md:text-5xl" data-reveal>{c.title}</h2>
          <p className="mx-auto mt-5 max-w-lg text-slate2/80" data-reveal>{c.subtitle}</p>
          <p className="mt-2 text-sm text-slate2/60" data-reveal>
            <a href={`mailto:${CONTACT_EMAIL}`} className="underline decoration-gold/40 hover:text-gold-deep">
              {CONTACT_EMAIL}
            </a>
          </p>
        </div>

        <div className="mt-12" data-reveal>
          <GlassCard className="p-7 md:p-9">
            {sent ? (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <span className="text-3xl text-gold">✦</span>
                <p className="font-display text-xl">{c.success}</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="flex flex-col gap-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <Input id="c-name" label={c.name} placeholder={c.namePh} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  <Input id="c-email" type="email" label={c.email} placeholder={c.emailPh} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="flex flex-col gap-1.5 text-left">
                  <label htmlFor="c-message" className="text-xs font-medium tracking-wide text-slate-500">{c.message}</label>
                  <textarea
                    id="c-message"
                    required
                    rows={5}
                    placeholder={c.messagePh}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="w-full resize-y rounded-xl border border-slate-200 bg-white/90 px-4 py-3 text-charcoal placeholder:text-slate-400 outline-none transition-colors duration-200 focus:border-gold focus:bg-white"
                  />
                </div>
                {error && <p className="text-xs text-red-500">{error}</p>}
                <Button type="submit" className="mt-2 w-full" disabled={busy}>
                  {busy ? "Sende …" : c.cta}
                </Button>
              </form>
            )}
          </GlassCard>
        </div>
      </div>
    </section>
  );
}
